import { useMemo } from "react";
import { Link as WouterLink } from "wouter";
import { ShieldCheck, Sparkles, AlertTriangle, Info } from "lucide-react";

/**
 * ArticleRenderer
 *
 * A small, dependency-free markdown renderer scoped to the Knowledge Center.
 * It supports:
 *   - Headings (#, ##, ###) with auto-generated id anchors and a TOC
 *   - Paragraphs, bold (**...**), italic (*...*), inline code (`...`)
 *   - Bullet lists (- / *) and ordered lists (1.)
 *   - Blockquotes (>)
 *   - Links [text](url) — restricted to http/https/mailto
 *   - Horizontal rules (---)
 *   - Fenced code blocks (```)
 *   - Video URLs on their own line (YouTube, Vimeo, .mp4) → embedded player
 *   - Admonitions:  ":::auditor ... :::"  and  ":::differentiator ... :::"
 *     (also: ":::note", ":::warning")
 *
 * All output is constructed via React elements (no dangerouslySetInnerHTML
 * with user-supplied content), so it is sanitized by construction.
 */

type Block =
  | { kind: "heading"; level: 1 | 2 | 3; text: string; id: string }
  | { kind: "paragraph"; text: string }
  | { kind: "list"; ordered: boolean; items: string[] }
  | { kind: "quote"; lines: string[] }
  | { kind: "hr" }
  | { kind: "code"; lang: string; text: string }
  | { kind: "video"; url: string }
  | { kind: "admonition"; flavor: "auditor" | "differentiator" | "note" | "warning"; lines: string[] };

const VIDEO_RX =
  /^(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=[\w-]+|youtu\.be\/[\w-]+|player\.vimeo\.com\/video\/\d+|vimeo\.com\/\d+)[^\s]*|https?:\/\/[^\s]+\.mp4)$/i;

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);
}

function parseBlocks(markdown: string): Block[] {
  const lines = markdown.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  const usedIds = new Set<string>();

  let i = 0;
  while (i < lines.length) {
    const raw = lines[i];
    const line = raw.trimEnd();

    // Skip blank lines
    if (line.trim() === "") {
      i++;
      continue;
    }

    // Fenced code
    if (line.startsWith("```")) {
      const lang = line.slice(3).trim();
      const buf: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith("```")) {
        buf.push(lines[i]);
        i++;
      }
      i++; // skip closing fence
      blocks.push({ kind: "code", lang, text: buf.join("\n") });
      continue;
    }

    // Admonition fences :::flavor
    if (line.startsWith(":::")) {
      const flavor = line.slice(3).trim().toLowerCase();
      const valid: Block["kind"] = "admonition";
      const validFlavors = ["auditor", "differentiator", "note", "warning"] as const;
      const f = (validFlavors as readonly string[]).includes(flavor)
        ? (flavor as "auditor" | "differentiator" | "note" | "warning")
        : "note";
      const buf: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith(":::")) {
        buf.push(lines[i]);
        i++;
      }
      i++; // skip closing
      blocks.push({ kind: valid, flavor: f, lines: buf });
      continue;
    }

    // Horizontal rule
    if (/^-{3,}$/.test(line.trim())) {
      blocks.push({ kind: "hr" });
      i++;
      continue;
    }

    // Headings
    const headingMatch = /^(#{1,3})\s+(.*)$/.exec(line);
    if (headingMatch) {
      const level = headingMatch[1].length as 1 | 2 | 3;
      const text = headingMatch[2].trim();
      let id = slugify(text);
      let n = 2;
      while (usedIds.has(id)) {
        id = `${slugify(text)}-${n++}`;
      }
      usedIds.add(id);
      blocks.push({ kind: "heading", level, text, id });
      i++;
      continue;
    }

    // Standalone video URL
    if (VIDEO_RX.test(line.trim())) {
      blocks.push({ kind: "video", url: line.trim() });
      i++;
      continue;
    }

    // Blockquote
    if (line.startsWith(">")) {
      const buf: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith(">")) {
        buf.push(lines[i].trim().replace(/^>\s?/, ""));
        i++;
      }
      blocks.push({ kind: "quote", lines: buf });
      continue;
    }

    // Lists
    if (/^\s*([-*]|\d+\.)\s+/.test(line)) {
      const ordered = /^\s*\d+\.\s+/.test(line);
      const items: string[] = [];
      while (i < lines.length && /^\s*([-*]|\d+\.)\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*([-*]|\d+\.)\s+/, "").trim());
        i++;
      }
      blocks.push({ kind: "list", ordered, items });
      continue;
    }

    // Paragraph — collect until blank or block-starting line
    const buf: string[] = [line];
    i++;
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !lines[i].startsWith("#") &&
      !lines[i].startsWith(">") &&
      !lines[i].startsWith("```") &&
      !lines[i].startsWith(":::") &&
      !/^\s*([-*]|\d+\.)\s+/.test(lines[i]) &&
      !/^-{3,}$/.test(lines[i].trim()) &&
      !VIDEO_RX.test(lines[i].trim())
    ) {
      buf.push(lines[i]);
      i++;
    }
    blocks.push({ kind: "paragraph", text: buf.join(" ") });
  }

  return blocks;
}

// Render inline markdown safely → React nodes
function renderInline(text: string): React.ReactNode[] {
  // Tokenize using regex placeholders, preserving order.
  // Links accept absolute URLs (http/https/mailto) AND in-app paths starting with `/`.
  const tokens: { kind: string; value: string; href?: string }[] = [];
  const RX = /(\[[^\]]+\]\((?:https?:\/\/|mailto:|\/)[^\s)]*\))|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(`[^`]+`)/g;
  let lastIdx = 0;
  let m: RegExpExecArray | null;
  while ((m = RX.exec(text)) !== null) {
    if (m.index > lastIdx) {
      tokens.push({ kind: "text", value: text.slice(lastIdx, m.index) });
    }
    if (m[1]) {
      const lm = /\[([^\]]+)\]\(((?:https?:\/\/|mailto:|\/)[^\s)]*)\)/.exec(m[1]);
      if (lm) tokens.push({ kind: "link", value: lm[1], href: lm[2] });
    } else if (m[2]) {
      tokens.push({ kind: "bold", value: m[2].slice(2, -2) });
    } else if (m[3]) {
      tokens.push({ kind: "italic", value: m[3].slice(1, -1) });
    } else if (m[4]) {
      tokens.push({ kind: "code", value: m[4].slice(1, -1) });
    }
    lastIdx = RX.lastIndex;
  }
  if (lastIdx < text.length) tokens.push({ kind: "text", value: text.slice(lastIdx) });

  return tokens.map((t, idx) => {
    switch (t.kind) {
      case "bold":
        return <strong key={idx}>{t.value}</strong>;
      case "italic":
        return <em key={idx}>{t.value}</em>;
      case "code":
        return (
          <code key={idx} className="rounded bg-muted px-1 py-0.5 font-mono text-sm">
            {t.value}
          </code>
        );
      case "link":
        if (t.href?.startsWith("/")) {
          return (
            <WouterLink
              key={idx}
              href={t.href}
              className="text-primary underline-offset-2 hover:underline"
              data-testid="link-article"
            >
              {t.value}
            </WouterLink>
          );
        }
        return (
          <a
            key={idx}
            href={t.href}
            target={t.href?.startsWith("http") ? "_blank" : undefined}
            rel="noopener noreferrer"
            className="text-primary underline-offset-2 hover:underline"
            data-testid="link-article"
          >
            {t.value}
          </a>
        );
      default:
        return <span key={idx}>{t.value}</span>;
    }
  });
}

function VideoEmbed({ url }: { url: string }) {
  // YouTube
  let yt: string | null = null;
  const ytWatch = /youtube\.com\/watch\?v=([\w-]+)/.exec(url);
  const ytShort = /youtu\.be\/([\w-]+)/.exec(url);
  if (ytWatch) yt = ytWatch[1];
  else if (ytShort) yt = ytShort[1];
  if (yt) {
    return (
      <div className="my-4 aspect-video w-full overflow-hidden rounded-lg border bg-muted">
        <iframe
          src={`https://www.youtube.com/embed/${yt}`}
          title="Embedded video"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="h-full w-full"
          data-testid="iframe-video"
        />
      </div>
    );
  }
  // Vimeo
  const vm = /vimeo\.com\/(?:video\/)?(\d+)/.exec(url);
  if (vm) {
    return (
      <div className="my-4 aspect-video w-full overflow-hidden rounded-lg border bg-muted">
        <iframe
          src={`https://player.vimeo.com/video/${vm[1]}`}
          title="Embedded video"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          className="h-full w-full"
          data-testid="iframe-video"
        />
      </div>
    );
  }
  // mp4 fallback
  return (
    <video controls className="my-4 w-full rounded-lg border bg-black" data-testid="video-mp4">
      <source src={url} type="video/mp4" />
    </video>
  );
}

function Admonition({ flavor, lines }: { flavor: "auditor" | "differentiator" | "note" | "warning"; lines: string[] }) {
  const meta = {
    auditor: {
      label: "For auditors & SecOps reviewers",
      cls: "border-blue-300 bg-blue-50 dark:border-blue-900/60 dark:bg-blue-950/30",
      Icon: ShieldCheck,
      iconCls: "text-blue-600 dark:text-blue-400",
      labelCls: "text-blue-900 dark:text-blue-200",
    },
    differentiator: {
      label: "Why this matters",
      cls: "border-amber-300 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/30",
      Icon: Sparkles,
      iconCls: "text-amber-600 dark:text-amber-400",
      labelCls: "text-amber-900 dark:text-amber-200",
    },
    note: {
      label: "Note",
      cls: "border-slate-300 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/40",
      Icon: Info,
      iconCls: "text-slate-600 dark:text-slate-300",
      labelCls: "text-slate-900 dark:text-slate-200",
    },
    warning: {
      label: "Warning",
      cls: "border-red-300 bg-red-50 dark:border-red-900/60 dark:bg-red-950/30",
      Icon: AlertTriangle,
      iconCls: "text-red-600 dark:text-red-400",
      labelCls: "text-red-900 dark:text-red-200",
    },
  }[flavor];
  const Icon = meta.Icon;
  return (
    <aside
      className={`my-4 rounded-lg border p-4 ${meta.cls}`}
      data-testid={`admonition-${flavor}`}
    >
      <div className={`mb-2 flex items-center gap-2 text-sm font-semibold ${meta.labelCls}`}>
        <Icon className={`h-4 w-4 ${meta.iconCls}`} />
        <span>{meta.label}</span>
      </div>
      <div className="space-y-2 text-sm leading-relaxed">
        {lines.filter((l) => l.trim()).map((l, i) => (
          <p key={i}>{renderInline(l)}</p>
        ))}
      </div>
    </aside>
  );
}

interface ArticleRendererProps {
  markdown: string;
  videoUrl?: string | null;
  showToc?: boolean;
  className?: string;
}

export function ArticleRenderer({
  markdown,
  videoUrl,
  showToc = true,
  className,
}: ArticleRendererProps) {
  const blocks = useMemo(() => parseBlocks(markdown || ""), [markdown]);
  const headings = useMemo(
    () =>
      blocks
        .filter((b): b is Extract<Block, { kind: "heading" }> => b.kind === "heading")
        .filter((b) => b.level <= 2),
    [blocks]
  );

  return (
    <div className={`flex flex-col gap-6 lg:flex-row ${className || ""}`}>
      {showToc && headings.length > 1 && (
        <nav
          className="lg:sticky lg:top-20 lg:order-last lg:w-56 lg:flex-shrink-0"
          aria-label="On this page"
          data-testid="article-toc"
        >
          <div className="rounded-md border bg-card p-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              On this page
            </div>
            <ul className="space-y-1 text-sm">
              {headings.map((h) => (
                <li key={h.id} className={h.level === 2 ? "pl-3" : ""}>
                  <a
                    href={`#${h.id}`}
                    className="text-muted-foreground hover:text-foreground"
                    data-testid={`toc-link-${h.id}`}
                  >
                    {h.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </nav>
      )}

      <article className="min-w-0 flex-1 space-y-4 text-foreground" data-testid="article-body">
        {videoUrl ? <VideoEmbed url={videoUrl} /> : null}
        {blocks.map((b, idx) => {
          switch (b.kind) {
            case "heading": {
              const Tag = (`h${b.level}` as unknown) as keyof JSX.IntrinsicElements;
              const sizes = {
                1: "text-3xl font-bold mt-8 mb-3",
                2: "text-2xl font-semibold mt-6 mb-2",
                3: "text-xl font-semibold mt-4 mb-2",
              }[b.level];
              return (
                <Tag id={b.id} key={idx} className={sizes} data-testid={`heading-${b.id}`}>
                  {b.text}
                </Tag>
              );
            }
            case "paragraph":
              return (
                <p key={idx} className="leading-relaxed">
                  {renderInline(b.text)}
                </p>
              );
            case "list":
              return b.ordered ? (
                <ol key={idx} className="list-decimal space-y-1 pl-6">
                  {b.items.map((it, i) => (
                    <li key={i}>{renderInline(it)}</li>
                  ))}
                </ol>
              ) : (
                <ul key={idx} className="list-disc space-y-1 pl-6">
                  {b.items.map((it, i) => (
                    <li key={i}>{renderInline(it)}</li>
                  ))}
                </ul>
              );
            case "quote":
              return (
                <blockquote
                  key={idx}
                  className="border-l-4 border-primary/40 bg-muted/40 px-4 py-2 italic text-muted-foreground"
                >
                  {b.lines.map((l, i) => (
                    <p key={i}>{renderInline(l)}</p>
                  ))}
                </blockquote>
              );
            case "hr":
              return <hr key={idx} className="my-6 border-border" />;
            case "code":
              return (
                <pre
                  key={idx}
                  className="overflow-x-auto rounded-md border bg-muted p-3 text-sm"
                >
                  <code>{b.text}</code>
                </pre>
              );
            case "video":
              return <VideoEmbed key={idx} url={b.url} />;
            case "admonition":
              return <Admonition key={idx} flavor={b.flavor} lines={b.lines} />;
            default:
              return null;
          }
        })}
      </article>
    </div>
  );
}
